import type { UserAttributes, VolunteerActivityAttributes } from '@/server/models'

import { SQL, type SQLFragment } from '@/client/query'
import { AUTO_APPROVED_SESSION_PREP_MAX_DURATION_SECONDS } from '@/client/services/volunteerActivity/constants'
import { SAT_BOOTCAMP_WORLD_SLUG } from '@/client/services/worlds/constants'

import {
  type AssociationDefinitions,
  type GroupBy,
  makeQueryModelV2,
  type QueryOrder,
  queryGroupByToSQL,
  queryOrderToSQL,
} from './common'
import type { VolunteerActivityType } from './constants'
import SessionHostQ from './SessionHostQ'
import UserQ from './UserQ'
import WorldQ from './WorldQ'

const VolunteerActivityQ = {
  ...makeQueryModelV2<VolunteerActivityAttributes>('volunteer_activity')([
    'id',
    'type',
    'user_id',
    'date',
    'duration_seconds',
    'description',
    'links',
    'approved_at',
    'reviewer_id',
    'requested_changes',
    'requested_changes_at',
    'last_submitted_at',
    'created_at',
    'deleted_at',
  ]),

  associations: (): AssociationDefinitions => ({
    SessionPrepSessionHost: {
      query: SessionHostQ,
      foreignKey: SessionHostQ.session_prep_volunteer_activity_id,
      sourceKey: VolunteerActivityQ.id,
    },
  }),

  where: (
    {
      id,
      userId,
      type,
      isApproved,
      groupBy = null,
      order = null,
      paranoid = true,
    }: {
      id?: number | null | SQLFragment<number>
      userId?: number | null | SQLFragment<number>
      type?: VolunteerActivityType[] | null
      isApproved?: boolean | null
      groupBy?: GroupBy | null
      order?: QueryOrder | null
      paranoid?: boolean
    },
    customWhere: SQLFragment<unknown> | null = null
  ): SQLFragment<VolunteerActivityAttributes> => SQL`
    FROM ${VolunteerActivityQ}
    WHERE
        ${id != null ? SQL`${VolunteerActivityQ.id} = ${id} AND` : SQL``}
        ${type != null ? SQL`${VolunteerActivityQ.type} = ANY(${type}) AND` : SQL``}
        ${userId != null ? SQL`${VolunteerActivityQ.user_id} = ${userId} AND` : SQL``}
        ${
          isApproved != null
            ? SQL`${VolunteerActivityQ.approved_at} IS ${
                isApproved ? SQL`NOT NULL` : SQL`NULL`
              } AND`
            : SQL``
        }
        ${paranoid ? SQL`${VolunteerActivityQ.deleted_at} IS NULL AND` : SQL``}
        ${customWhere != null ? customWhere : SQL`TRUE`}
    ${queryGroupByToSQL(groupBy)}
    ${queryOrderToSQL(order)}
    `,

  isApproved: (): SQLFragment<boolean> => SQL`
    (${VolunteerActivityQ.approved_at} IS NOT NULL)
  `,

  requiresChangesBeforeApproval: (): SQLFragment<boolean> => SQL`
    (${VolunteerActivityQ.requested_changes_at} IS NOT NULL AND ${VolunteerActivityQ.requested_changes_at} > ${VolunteerActivityQ.last_submitted_at})
  `,

  getReviewer: (): SQLFragment<UserAttributes | null> =>
    UserQ.where({ id: VolunteerActivityQ.reviewer_id }),

  /**
   * Session Prep hours associated with SAT Bootcamps that meet certain criteria are auto-approved once a week. See listeners/cron/sat-bootcamp.ts.
   */
  isAutoApprovableSessionPrep: (): SQLFragment<boolean> => SQL`
    (
      ${VolunteerActivityQ.type} = 'host-session-prep' AND
      ${VolunteerActivityQ.approved_at} IS NULL AND
      ${VolunteerActivityQ.requested_changes_at} IS NULL AND
      ${VolunteerActivityQ.duration_seconds} <= ${AUTO_APPROVED_SESSION_PREP_MAX_DURATION_SECONDS} AND
      EXISTS(
        SELECT 1 ${SessionHostQ.where(
          {
            include: [
              {
                association: 'Session',
                required: true,
                include: [
                  {
                    association: 'World',
                    required: true,
                    where: SQL`${WorldQ.slug} = ${SAT_BOOTCAMP_WORLD_SLUG}`,
                  },
                ],
              },
            ],
          },
          SQL`${SessionHostQ.session_prep_volunteer_activity_id} = ${VolunteerActivityQ.id}`
        )}
      )
    )
  `,

  getTutoringMinutes: (): SQLFragment<number> => SQL`
    SUM(${VolunteerActivityQ.duration_seconds})::NUMERIC / 60
    ${VolunteerActivityQ.where({
      userId: UserQ.id,
      type: ['host-session'],
      isApproved: true,
    })}
  `,
}

export default VolunteerActivityQ
